import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import ColorizedLogText from "./ColorizedLogText";
import LogFailureDetails from "./LogFailureDetails";
import { HighlightedText } from "../preview/searchUtils";
import {
  LOG_STATE_CHIP_COLOR,
  LOG_STATE_COLORS,
  LogRecordDto,
  ManualResolutionKind,
  ROSTER_NAME_HINT,
  failureReasonLine,
  formatLogDate,
  formatLogTime,
  isManuallyResolved,
  recordKeys,
  recordNote,
  recordSummary,
  recordTitle,
  resolvedBadgeLabel,
  resolvedBadgeTooltip,
  titleIsFromRoster,
} from "./logRecords";
import { resolutionWording } from "./manualResolution";
import { LOG_ROW_GONE_HINT, LOG_ROW_HINT, rosterRowLabel } from "./rosterFile";

const STATE_LABELS: Record<LogRecordDto["state"], string> = {
  success: "SUCCESS",
  error: "ERROR",
  warning: "WARNING",
  unknown: "UNKNOWN",
};

/**
 * One row per record: what it is, what state it is in now, and - on click - the
 * exact text the bot wrote. A row that says SUCCESS while carrying a RESOLVED
 * badge is an employee (or a file) that failed earlier and was fixed since; the
 * badge is what stops that history from reading as a live problem. RESOLVED
 * (manually) is the same thing said by an operator instead of a later run. A
 * failing employee says why under its numbers, and in full when opened.
 */
function LogRecordRow({
  record,
  query,
  showTask,
  jobId,
  onResolve,
  resolveKind = "upload",
  resolveDisabledReason,
}: {
  record: LogRecordDto;
  query: string;
  showTask: boolean;
  jobId: number | null;
  onResolve?: (record: LogRecordDto) => void;
  resolveKind?: ManualResolutionKind;
  resolveDisabledReason?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const title = recordTitle(record);
  const fromRoster = titleIsFromRoster(record);
  const summary = recordSummary(record);
  const reasonLine = failureReasonLine(record);
  const note = recordNote(record);
  const stateColor = LOG_STATE_COLORS[record.state];
  // The action belongs to an employee row: still in error, or resolved by hand
  // (to see or undo the marking). Rows the endpoint cannot address get none.
  const offerResolve =
    onResolve != null &&
    record.stream === "status" &&
    Boolean(record.employee_id) &&
    (record.state === "error" || isManuallyResolved(record));

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        px: 1.5,
        py: 0.75,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          cursor: "pointer",
          minWidth: 0,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          rowGap: 0.5,
        }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Box sx={{ color: "text.disabled", display: "flex", alignItems: "center" }}>
          {open ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </Box>
        <Tooltip title={record.level || "(no level)"}>
          <Chip
            size="small"
            label={STATE_LABELS[record.state]}
            color={LOG_STATE_CHIP_COLOR[record.state]}
            variant="outlined"
            sx={{ height: 20, minWidth: 78, fontSize: "0.68rem", fontWeight: 600 }}
          />
        </Tooltip>
        <Tooltip title={formatLogDate(record.timestamp) || "(no date)"}>
          <Typography
            variant="caption"
            sx={{ fontFamily: "monospace", color: "text.secondary", flexShrink: 0 }}
          >
            {formatLogTime(record.timestamp)}
          </Typography>
        </Tooltip>
        <Box
          sx={{
            minWidth: 0,
            flexGrow: 1,
            // Once the row wraps, the title owns its own line and the badges move
            // below it, instead of the badges pushing the title out of existence.
            flexBasis: { xs: "100%", sm: "auto" },
            order: { xs: 3, sm: 0 },
          }}
        >
          <Tooltip title={fromRoster ? `${title} - ${ROSTER_NAME_HINT}` : title}>
            <Typography
              variant="body2"
              sx={{
                color: stateColor,
                fontWeight: 500,
                fontStyle: fromRoster ? "italic" : undefined,
                opacity: fromRoster ? 0.75 : undefined,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <HighlightedText text={title} query={query} />
            </Typography>
          </Tooltip>
          {summary ? (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <HighlightedText text={summary} query={query} />
            </Typography>
          ) : null}
          {reasonLine ? (
            <Tooltip title={reasonLine}>
              <Typography
                variant="caption"
                sx={{
                  color: stateColor,
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <HighlightedText text={reasonLine} query={query} />
              </Typography>
            </Tooltip>
          ) : null}
          {note ? (
            <Tooltip title={note}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontStyle: "italic",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <HighlightedText text={`Note: ${note}`} query={query} />
              </Typography>
            </Tooltip>
          ) : null}
        </Box>
        <Stack
          direction="row"
          sx={{
            // On its own line once the row wraps, and wrapping inside it: kept at
            // its natural width it ran past the row on a phone.
            flexShrink: { xs: 1, sm: 0 },
            flexBasis: { xs: "100%", sm: "auto" },
            flexWrap: "wrap",
            gap: 0.5,
            minWidth: 0,
            order: { xs: 4, sm: 0 },
          }}
        >
          {record.inner_number != null ? (
            <Tooltip title={record.in_roster === false ? LOG_ROW_GONE_HINT : LOG_ROW_HINT}>
              <Chip
                size="small"
                variant="outlined"
                label={rosterRowLabel(record.inner_number)}
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            </Tooltip>
          ) : null}
          {record.stream === "status" && record.s3_name ? (
            <Tooltip title="Folder in Data Storage (row of the Matches file)">
              <Chip
                size="small"
                variant="outlined"
                label={record.s3_name}
                sx={{ height: 20, fontSize: "0.68rem", maxWidth: 240 }}
              />
            </Tooltip>
          ) : null}
          {record.employee_id ? (
            <Tooltip title="Employee id">
              <Chip
                size="small"
                variant="outlined"
                label={record.employee_id}
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            </Tooltip>
          ) : null}
          {record.resolved || isManuallyResolved(record) ? (
            <Tooltip title={resolvedBadgeTooltip(record)}>
              <Chip
                size="small"
                color="success"
                label={resolvedBadgeLabel(record)}
                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600 }}
              />
            </Tooltip>
          ) : null}
          {record.attempts > 1 ? (
            <Tooltip title={`${record.attempts} attempts on this employee/file`}>
              <Chip
                size="small"
                variant="outlined"
                label={`x${record.attempts}`}
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            </Tooltip>
          ) : null}
          {showTask && record.from_deleted_task && record.source_task_id != null ? (
            <Tooltip
              title={
                record.owner_task_id != null
                  ? `Written by task ${record.source_task_id}, which was deleted; those employees belong to task ${record.owner_task_id} now`
                  : `Written by task ${record.source_task_id}, which was deleted - its work still counts for the job`
              }
            >
              <Chip
                size="small"
                variant="outlined"
                label={`deleted task ${record.source_task_id}`}
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  color: "text.disabled",
                  borderStyle: "dashed",
                }}
              />
            </Tooltip>
          ) : showTask && record.owner_task_id != null ? (
            <Tooltip
              title={
                record.source_task_id != null &&
                record.source_task_id !== record.owner_task_id
                  ? `Owned by task ${record.owner_task_id} now, written by task ${record.source_task_id}`
                  : `Task ${record.owner_task_id}`
              }
            >
              <Chip
                size="small"
                variant="outlined"
                label={`task ${record.owner_task_id}`}
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            </Tooltip>
          ) : null}
          {offerResolve ? (
            <Tooltip
              title={
                resolveDisabledReason ??
                (record.state === "error"
                  ? resolutionWording(resolveKind).resolveHint
                  : resolutionWording(resolveKind).reviewHint)
              }
            >
              {/* A disabled button swallows clicks, so the span stops them
                  before they reach the row and toggle the raw text. */}
              <span onClick={(event) => event.stopPropagation()}>
                <Button
                  size="small"
                  variant="outlined"
                  color={record.state === "error" ? "error" : "success"}
                  startIcon={<TaskAltOutlinedIcon sx={{ fontSize: 14 }} />}
                  disabled={resolveDisabledReason != null}
                  onClick={(event) => {
                    event.stopPropagation();
                    onResolve(record);
                  }}
                  sx={{
                    height: 22,
                    py: 0,
                    px: 1,
                    fontSize: "0.68rem",
                    textTransform: "none",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  Manually resolved
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>
      {open ? <LogFailureDetails record={record} jobId={jobId} query={query} /> : null}
      {open ? (
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 0.75,
            ml: 4,
            p: 1,
            bgcolor: "action.hover",
            borderRadius: 1,
            fontFamily: "monospace",
            fontSize: "0.74rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          <ColorizedLogText text={record.raw} />
        </Box>
      ) : null}
    </Box>
  );
}

export default function LogRecordList({
  records,
  query,
  showTask,
  jobId = null,
  loading,
  emptyLabel,
  onResolve,
  resolveKind,
  resolveDisabledReason,
}: {
  records: LogRecordDto[];
  query: string;
  showTask: boolean;
  /** The job the records belong to: an error screenshot is asked for through it. */
  jobId?: number | null;
  loading: boolean;
  emptyLabel: string;
  /** Opens "Manually resolved" for an employee row; absent = no action offered. */
  onResolve?: (record: LogRecordDto) => void;
  /** Which job the action resolves for - it words the action's tooltip. */
  resolveKind?: ManualResolutionKind;
  /** Why the action is disabled (read-only, tasks running); null when it may act. */
  resolveDisabledReason?: string | null;
}) {
  if (loading && records.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", py: 4 }}>
        <CircularProgress size={22} />
      </Stack>
    );
  }
  if (records.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", py: 4 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {emptyLabel}
        </Typography>
      </Stack>
    );
  }
  const keys = recordKeys(records);
  return (
    <Box>
      {records.map((record, index) => (
        <LogRecordRow
          key={keys[index]}
          record={record}
          query={query}
          showTask={showTask}
          jobId={jobId}
          onResolve={onResolve}
          resolveKind={resolveKind}
          resolveDisabledReason={resolveDisabledReason}
        />
      ))}
    </Box>
  );
}
